import React, { useState, useEffect } from 'react';
// import { FaTrophy } from 'react-icons/fa'; // Ensure react-icons is installed or use fallback
import { getLastResults, getDetailResults } from '@/services/casinoApi';

import "./Result.css";
import ResultModalLayout from '@/components/ResultModalLayout';

// Helper function to map card string to image path
const getCardImage = (cardString) => {
  if (!cardString) return null;
  // Adjust path to match where images are stored in Admin
  return `/assets/img/cards/${cardString}.png`;
};

// Helper function to parse card string and split into Player A and Player B cards
const parseCards = (cardString) => {
  if (!cardString) return { playerA: [], playerB: [] };

  const cards = cardString.split(',');
  const playerACards = cards.slice(0, 3).map(card => getCardImage(card.trim()));
  const playerBCards = cards.slice(3, 6).map(card => getCardImage(card.trim()));

  return { playerA: playerACards, playerB: playerBCards };
};

// Helper function to parse rdesc
const parseRdesc = (rdesc) => {
  if (!rdesc) return {};

  const parts = rdesc.split('#');
  const winner = parts[0] || "";

  // Part 1: 3 Baccarat (may contain ~ with score)
  const baccarat3Full = parts[1] || "";
  let baccarat3 = "";
  let baccaratScore = "";
  if (baccarat3Full.includes('~')) {
    const baccaratParts = baccarat3Full.split('~');
    baccarat3 = baccaratParts[0] || "";
    baccaratScore = baccaratParts[1] || "";
  } else {
    baccarat3 = baccarat3Full;
  }

  // Part 2: Total
  const total = parts[2] || "";

  // Part 3: Pair Plus - skip (always empty, no data from backend)
  // Part 4: Red Black (or Part 3 if Pair Plus is empty)
  const redBlack = parts[4] || parts[3] || "";

  return { winner, baccarat3, baccaratScore, total, redBlack };
};

const ResultTeen20c = ({
  data = [],
  gameData = {},
  playerA = {},
  playerB = {},
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults('teen20c');
        if (response?.data?.data?.res) {
          // Map win values: "1" = "A", "2" = "B"
          const mappedResults = response.data.data.res.map(item => ({
            mid: item.mid,
            win: item.win === "1" ? "A" : item.win === "2" ? "B" : item.win
          }));
          setLastResults(mappedResults);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };

    fetchLastResults();
  }, []);

  const handleResultClick = async (result, index) => {
    setSelectedResult(result);
    setLoading(true);

    // First, ensure we have last results
    if (lastResults.length === 0) {
      try {
        const response = await getLastResults('teen20c');
        if (response?.data?.data?.res) {
          const mappedResults = response.data.data.res.map(item => ({
            mid: item.mid,
            win: item.win === "1" ? "A" : item.win === "2" ? "B" : item.win
          }));
          setLastResults(mappedResults);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    }

    // Find the mid for this result
    let mid = null;
    if (index < lastResults.length) {
      mid = lastResults[index]?.mid;
    } else if (index < data.length + (gameData?.lrs?.length || 0)) {
      mid = gameData?.data?.data?.mid || gameData?.mid;
    }

    // Fetch detail results if we have a mid
    if (mid) {
      try {
        const response = await getDetailResults('teen20c', mid.toString());
        if (response?.data?.data?.t1) {
          setDetailResult(response.data.data.t1);
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      }
    }

    setLoading(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  // Format modal data based on selected result
  const getModalData = () => {
    if (!selectedResult) return null;

    // Use detailResult if available, otherwise fallback to defaults
    const cards = detailResult?.card ? parseCards(detailResult.card) : {
      playerA: playerA?.cards || [],
      playerB: playerB?.cards || []
    };

    const rdescData = detailResult?.rdesc ? parseRdesc(detailResult.rdesc) : {};
    const winner = detailResult?.winnat || (selectedResult === "A" ? "Player A" : selectedResult === "B" ? "Player B" : "");

    // Determine winner from API data - check win field first, then winnat, then selectedResult
    const winValue = detailResult?.win;
    const isPlayerAWinner = winValue === "1" || winner === "Player A" || selectedResult === "A";
    const isPlayerBWinner = winValue === "2" || winner === "Player B" || selectedResult === "B";

    return {
      roundId: detailResult?.rid || gameData?.data?.data?.mid || gameData?.mid || "157250124095658",
      matchTime: detailResult?.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      playerA: {
        cards: cards.playerA,
        isWinner: isPlayerAWinner,
      },
      playerB: {
        cards: cards.playerB,
        isWinner: isPlayerBWinner,
      },
      resultDetails: {
        winner: winner,
        baccarat3: rdescData.baccarat3 || "",
        baccaratScore: rdescData.baccaratScore || "",
        total: rdescData.total || "",
        pairPlus: "", // Always empty - no data from backend
        redBlack: rdescData.redBlack || "",
      },
    };
  };

  const modalData = getModalData();

  // Combine API results with fallback data
  const allResults = lastResults.length > 0
    ? lastResults.map(r => r.win)
    : [...data, ...(gameData?.lrs || [])];

  return (
    <>
      <div className="result-row">
        {allResults.map((res, index) => (
          <div
            key={index}
            className={`result-circle ${res === "A" ? "yes" : "no"}`}
            onClick={() => handleResultClick(res, index)}
            style={{ cursor: 'pointer' }}
          >
            {res}
          </div>
        ))}
      </div>

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="20-20 TEENPATTI C"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          <div>
            {/* Round ID */}
            <h6 className="text-right round-id" style={{ marginBottom: '1rem' }}>
              <b>Round Id:</b> {modalData.roundId}
            </h6>

            {/* Player Cards Section */}
            <div className="flex flex-col md:flex-row gap-4">
              {/* Player A */}
              <div className="flex-1">
                <h4 className="text-center font-bold mb-2">Player A</h4>
                <div className="text-center flex justify-center gap-1">
                  {modalData.playerA?.cards?.map((card, index) => (
                    card && (
                      <img
                        key={index}
                        src={card}
                        alt={`Player A Card ${index + 1}`}
                        onError={(e) => {
                          const cardName = card.split('/').pop();
                          const fallbackPath = `/assets/img/card-results/${cardName}`;
                          if (e.target.src !== fallbackPath) {
                            e.target.src = fallbackPath;
                          } else {
                            e.target.style.display = 'none';
                          }
                        }}
                        style={{ maxWidth: '40px', height: 'auto' }}
                      />
                    )
                  ))}
                </div>
                {modalData.playerA?.isWinner && (
                  <div className="winner-label mt-2 text-center text-green-500 font-bold">
                    <span>
                      Winner
                    </span>
                  </div>
                )}
              </div>

              {/* Player B */}
              <div className="flex-1">
                <h4 className="text-center font-bold mb-2">Player B</h4>
                <div className="text-center flex justify-center gap-1">
                  {modalData.playerB?.cards?.map((card, index) => (
                    card && (
                      <img
                        key={index}
                        src={card}
                        alt={`Player B Card ${index + 1}`}
                        onError={(e) => {
                          const cardName = card.split('/').pop();
                          const fallbackPath = `/assets/img/card-results/${cardName}`;
                          if (e.target.src !== fallbackPath) {
                            e.target.src = fallbackPath;
                          } else {
                            e.target.style.display = 'none';
                          }
                        }}
                        style={{ maxWidth: '40px', height: 'auto' }}
                      />
                    )
                  ))}
                </div>
                {modalData.playerB?.isWinner && (
                  <div className="winner-label mt-2 text-center text-green-500 font-bold">
                    <span>
                      Winner
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultTeen20c;
